import { PlatformCreateOrganizationDTO, PlatformCreateOrganizationResponseDTO } from 'types/platform'
import { useCallback, useState } from 'react'
import { postWithStatus, postWithToken } from './http'
import { Organization } from 'types/organization'

// At the moment a token is not required for creating an organization via signup
export function usePlatform(token: string) {
  const [isCreatingOrganization, setIsCreatingOrganization] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const createOrganization = useCallback(
    async (request: PlatformCreateOrganizationDTO): Promise<PlatformCreateOrganizationResponseDTO | null> => {
      setIsCreatingOrganization(true)
      setError(null)

      try {
        // const response = await postWithTokenWithStatus<Organization>('platform/organizations', token, request)
        const result = await postWithStatus('signup', request)
        if (result.status < 200 || result.status >= 300) {
          const response: PlatformCreateOrganizationResponseDTO = {
            status: result.status,
            message: '',
            error: true,
            errorCode: '',
            organization: result.data as Organization,
            stripeSession: '',
          }
          return response
        } else {
          const response = result.data as PlatformCreateOrganizationResponseDTO
          return response
        }
      } catch (error: unknown) {
        console.error(error)
      } finally {
        setIsCreatingOrganization(false)
      }

      return null
    },
    [token]
  )

  const createCheckoutSession = useCallback(
    async (params: {
      organizationId: string
      product: string
      sizeTier: string
      stripeCustomerId: string
    }): Promise<{ checkoutUrl: string }> => {
      const result = await postWithStatus<{ checkoutUrl: string }>('signup/checkout', params)
      if (result.status < 200 || result.status >= 300) {
        throw new Error('Failed to create checkout session')
      }
      return result.data as { checkoutUrl: string }
    },
    []
  )

  const createAccountCheckoutSession = useCallback(
    async (product: string, sizeTier: string): Promise<{ checkoutUrl: string }> => {
      if (!token) throw new Error('Not authenticated')
      const result = await postWithToken('account/checkout', token, { product, sizeTier })
      if (!result?.checkoutUrl) {
        throw new Error('Failed to create checkout session')
      }
      return result as { checkoutUrl: string }
    },
    [token]
  )

  const updateSubscriptionPlan = useCallback(
    async (
      product: string,
      sizeTier: string
    ): Promise<{ subscriptionId: string; product: string; sizeTier: string; status: string }> => {
      if (!token) throw new Error('Not authenticated')
      return postWithToken('account/update-subscription', token, { product, sizeTier })
    },
    [token]
  )

  const cancelSubscription = useCallback(async (): Promise<{
    cancelledAt: string
    periodEnd: string
    status: string
    subscriptionId: string
  }> => {
    if (!token) throw new Error('Not authenticated')
    return postWithToken('account/cancel-subscription', token, {})
  }, [token])

  return {
    createOrganization,
    createCheckoutSession,
    createAccountCheckoutSession,
    updateSubscriptionPlan,
    cancelSubscription,
    isCreatingOrganization,
    error,
  }
}
